import useProcess from './useProcess';
import { useCallback, useEffect, useRef } from 'react';
import { CancelablePromise, makePromiseCancelable } from '../Helper/PromiseHelper';
import { enqueueSnackbar } from 'notistack';

type RequestFunction<T> = (
  apiFn: () => Promise<T>,
  onAccept?: (responseContent?: T) => void,
  onError?: (error: Error) => void,
) => void;

/**
 * Hook for api calls, based on "useTransition".
 *
 * Handles "canceling" of started request promises automatically.
 *
 * @todo implement response code mapping (requires ApiSDK change)
 * @returns [isProcessing, startRequest]
 */
export default function useRequest<T = unknown>(): [boolean, RequestFunction<T>] {
  const [isRequesting, startRequest] = useProcess();
  const promiseRef = useRef<CancelablePromise<T> | null>(null);

  useEffect(
    () => () => {
      if (promiseRef.current) {
        promiseRef.current.cancel();
      }
    },
    [],
  );

  /**
   * Calls the passed function and sets the state variable "true" until the function is resolved or rejected.
   *
   * @param apiFn the api request function
   * @param onAccept success callback, passes requested entity as parameter
   * @param onError error callback, passes error as parameter
   */
  const requestFn = useCallback(
    (apiFn: () => Promise<T>, onAccept?: (entity?: T) => void, onError?: (error: Error) => void) => {
      if (promiseRef.current) {
        promiseRef.current.cancel();
      }
      startRequest(async () => {
        try {
          promiseRef.current = makePromiseCancelable<T>(apiFn());
          const result = await promiseRef.current.promise;
          if (onAccept) {
            onAccept(result);
          }
        } catch (e) {
          if ((e as { isCanceled: boolean }).isCanceled === true) {
            // discard cancelled promise
            //console.log('[REQUEST] discarded promise:', apiFn.toString());
            return;
          }
          const error = e as Error;
          if (onError !== undefined) {
            onError(error);
          } else {
            enqueueSnackbar(error.message, { variant: 'error' });
          }
        }
      });
    },
    [],
  );

  return [isRequesting, requestFn];
}
